import React, { memo, useCallback, useEffect, useRef, useState } from "react";

import { classNames, useTranslate } from "helpers";
import { CheckBox } from "ui/components/CheckBox";
import { FleetVehicleStatusEnum } from "services/api/fleet/vehicles/types";
import {
  ALL_VEHICLE_STATUSES,
  DEFAULT_VEHICLE_STATUS_FILTER,
  FLEET_VEHICLE_STATUS_CONFIG,
  VEHICLE_STATUS_GROUPS,
} from "services/api/fleet/vehicles/consts";

import { VehicleStatusDropdownProps } from "./types";

/**
 * Višestruki odabir statusa vozila za filtriranje pretrage.
 * Izgled prati VehicleStatusModal — obojena točka i uvučeni pod-statusi —
 * uz kvačice jer se ovdje bira više statusa odjednom.
 * Označavanje roditelja povlači i sve njegove pod-statuse.
 */
const VehicleStatusDropdown: React.FC<VehicleStatusDropdownProps> = (props) => {
  const {
    name,
    label,
    placeholder = "Status",
    disabled,
    required,
    error,
    width,
    value,
    singleSelect,
    onChange,
  } = props;

  // Kad je nešto odabrano, greška se gasi — očistit će je iduća validacija forme.
  const isError = !!error && value.length === 0;

  const { t } = useTranslate();

  const wrapperRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const handleToggleOpen = useCallback(() => {
    if (disabled) return;
    setOpen((prev) => !prev);
  }, [disabled]);

  // Jedan status — klik zamjenjuje odabir i zatvara panel.
  const handleSingleSelect = useCallback(
    (status: FleetVehicleStatusEnum) => {
      onChange(name, [status]);
      setOpen(false);
    },
    [name, onChange]
  );

  // Roditelj se uključuje/isključuje zajedno sa svim svojim pod-statusima.
  const handleGroupToggle = useCallback(
    (status: FleetVehicleStatusEnum, subStatuses: FleetVehicleStatusEnum[]) => {
      if (singleSelect) {
        handleSingleSelect(status);
        return;
      }

      const group = [status, ...subStatuses];
      const isSelected = value.includes(status);

      const next = isSelected
        ? value.filter((item) => !group.includes(item as FleetVehicleStatusEnum))
        : [...new Set([...value, ...group])];

      onChange(name, next);
    },
    [name, onChange, value, singleSelect, handleSingleSelect]
  );

  const handleStatusToggle = useCallback(
    (status: FleetVehicleStatusEnum) => {
      if (singleSelect) {
        handleSingleSelect(status);
        return;
      }

      const next = value.includes(status)
        ? value.filter((item) => item !== status)
        : [...value, status];

      onChange(name, next);
    },
    [name, onChange, value, singleSelect, handleSingleSelect]
  );

  const handleSelectAll = useCallback(() => {
    onChange(name, [...ALL_VEHICLE_STATUSES]);
  }, [name, onChange]);

  const handleClearAll = useCallback(() => {
    onChange(name, []);
  }, [name, onChange]);

  // Varijanta koja vraća na zadani filter (sve osim prodanih) umjesto praznog odabira.
  // const handleResetToDefault = useCallback(() => {
  //   onChange(name, [...DEFAULT_VEHICLE_STATUS_FILTER]);
  // }, [name, onChange]);

  const widthClassName = () => {
    switch (width) {
      case "min":
        return "w-24";
      case "small":
        return "w-48";
      case "medium":
        return "w-96";
      default:
        return "w-full";
    }
  };

  const triggerText = () => {
    if (value.length === 0) return placeholder;

    // Jedan odabrani status uvijek se ispisuje imenom (vrijedi i za pod-statuse).
    if (value.length === 1) return t(FLEET_VEHICLE_STATUS_CONFIG[value[0] as FleetVehicleStatusEnum].label);

    if (value.length === ALL_VEHICLE_STATUSES.length) return "Svi statusi";

    // Sažetak nabraja samo root statuse da natpis ostane kratak.
    const roots = VEHICLE_STATUS_GROUPS.filter((group) => value.includes(group.status));

    if (roots.length === 1 && value.length <= 1 + roots[0].subStatuses.length) {
      return t(FLEET_VEHICLE_STATUS_CONFIG[roots[0].status].label);
    }

    return `${value.length} statusa`;
  };

  /**
   * Točka u boji na okidaču — samo kad je odabran točno jedan status, ili kad su
   * odabrani roditelj i sva njegova djeca (tada svi dijele istu boju).
   */
  const triggerDotColor = (): string | null => {
    if (value.length === 0 || value.length === ALL_VEHICLE_STATUSES.length) return null;

    if (value.length === 1) {
      return FLEET_VEHICLE_STATUS_CONFIG[value[0] as FleetVehicleStatusEnum].dotColor;
    }

    const roots = VEHICLE_STATUS_GROUPS.filter((group) => value.includes(group.status));

    if (roots.length === 1 && value.length <= 1 + roots[0].subStatuses.length) {
      return FLEET_VEHICLE_STATUS_CONFIG[roots[0].status].dotColor;
    }

    return null;
  };

  const renderStatusRow = (
    status: FleetVehicleStatusEnum,
    onClick: () => void,
    isSubStatus: boolean
  ) => {
    const config = FLEET_VEHICLE_STATUS_CONFIG[status];
    const checked = value.includes(status);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      onClick();
    };

    return (
      // Red je div, a ne button, jer CheckBox renderira <label> s <input> —
      // interaktivni element ne smije stajati unutar <button>.
      <div
        key={status}
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        className={classNames(
          "w-full flex items-center gap-3 px-3 py-2 text-left transition-colors rounded-sm hover:bg-slate-50 cursor-pointer",
          isSubStatus ? "pl-9" : ""
        )}
      >
        {/* pointer-events-none — klik ide na cijeli red, da se odabir ne okine dvaput. */}
        <span className="pointer-events-none flex">
          <CheckBox checked={checked} />
        </span>
        <span className={classNames("w-3 h-3 rounded-full flex-shrink-0", config.dotColor)} />
        <span className="text-sm font-medium text-slate-700 whitespace-nowrap">
          {t(config.label)}
        </span>
      </div>
    );
  };

  return (
    <div ref={wrapperRef} className={classNames(widthClassName(), "relative min-w-full")}>
      {label && (
        <label className="block text-sm mb-2 font-medium leading-6 text-[#344054]">
          {label}
          {required && <span className="text-red-500"> *</span>}
        </label>
      )}

      <button
        type="button"
        onClick={handleToggleOpen}
        disabled={disabled}
        className={classNames(
          widthClassName(),
          "relative cursor-pointer rounded-sm pl-3 pr-3 py-3 text-left sm:text-sm sm:leading-6 ring-1 focus:outline-none",
          isError
            ? "ring-red-500 bg-red-50 text-red-600"
            : "ring-slate-300 bg-slate-50 text-gray-800",
          disabled ? "bg-gray-100 cursor-default" : ""
        )}
      >
        <span className="flex items-center gap-2 w-full pr-4">
          {triggerDotColor() && (
            <span
              className={classNames(
                "w-3 h-3 rounded-full flex-shrink-0",
                triggerDotColor()!
              )}
            />
          )}
          <span
            className={classNames(
              "block truncate text-sm",
              value.length === 0 ? "text-gray-400" : "text-gray-800"
            )}
          >
            {triggerText()}
          </span>
        </span>
        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
          <svg className="h-5 w-5 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M10 3a.75.75 0 01.55.24l3.25 3.5a.75.75 0 11-1.1 1.02L10 4.852 7.3 7.76a.75.75 0 01-1.1-1.02l3.25-3.5A.75.75 0 0110 3zm-3.76 9.2a.75.75 0 011.06.04l2.7 2.908 2.7-2.908a.75.75 0 111.1 1.02l-3.25 3.5a.75.75 0 01-1.1 0l-3.25-3.5a.75.75 0 01.04-1.06z"
              clipRule="evenodd"
            />
          </svg>
        </span>
      </button>

      {/* Panel je širi od okidača da najduži nazivi (npr. "Nedostupno - Dokumentacija")
          stanu u jedan red i uz uvlaku pod-statusa. */}
      {open && (
        <div className="absolute left-0 z-20 mt-1 w-[340px] rounded-sm bg-white shadow-2xl ring-1 ring-black ring-opacity-5">
          {!singleSelect && (
            <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
              <button
                type="button"
                onClick={handleSelectAll}
                className="text-xs font-bold text-slate-700 hover:text-slate-900"
              >
                Označi sve
              </button>
              <button
                type="button"
                onClick={handleClearAll}
                className="text-xs font-bold text-slate-700 hover:text-slate-900"
              >
                Očisti sve
              </button>
            </div>
          )}

          <div className="flex flex-col gap-1 p-2 max-h-80 overflow-auto">
            {VEHICLE_STATUS_GROUPS.map((group) => {
              return (
                <div key={group.status}>
                  {renderStatusRow(
                    group.status,
                    () => handleGroupToggle(group.status, group.subStatuses),
                    false
                  )}
                  {group.subStatuses.length > 0 && (
                    <div className="flex flex-col gap-1 mt-1">
                      {group.subStatuses.map((subStatus) =>
                        renderStatusRow(
                          subStatus,
                          () => handleStatusToggle(subStatus),
                          true
                        )
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {isError && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
};

export default memo(VehicleStatusDropdown);
